package com.example.positionahead.data

import com.example.positionahead.domain.PerformanceCalculator
import com.example.positionahead.domain.resolveLiveQuotePrice
import com.example.positionahead.model.PricePoint
import com.example.positionahead.model.Quote
import com.example.positionahead.model.SymbolPerformance
import com.example.positionahead.model.SymbolPriceHistory
import com.russhwolf.settings.Settings
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class StockQuoteService(
    private val client: HttpClient,
    private val settings: Settings,
) {
    var apiKey: String
        get() = settings.getStringOrNull(KEY_API)
            ?: settings.getStringOrNull(LEGACY_KEY_API)
            ?: ""
        set(value) {
            settings.remove(LEGACY_KEY_API)
            if (value.isBlank()) settings.remove(KEY_API) else settings.putString(KEY_API, value.trim())
        }

    val usesLiveQuotes: Boolean get() = apiKey.isNotBlank()

    suspend fun fetchQuotes(
        symbols: List<String>,
        allowEstimatedFallback: Boolean = true,
    ): Map<String, Quote> {
        if (symbols.isEmpty()) return emptyMap()

        val distinct = symbols.distinct().map { it.uppercase() }
        val fetched = if (usesLiveQuotes) {
            fetchLiveQuotes(distinct, allowEstimatedFallback)
        } else {
            fetchMockQuotes(distinct, pricedForDisplay = true)
        }

        return distinct.associateWith { symbol ->
            fetched[symbol]?.takeIf { it.price > 0 }
                ?: when {
                    allowEstimatedFallback -> buildFallbackQuote(symbol, pricedForDisplay = false)
                    else -> Quote(symbol = symbol, price = 0.0, name = symbol, isLivePrice = false)
                }
        }.mapValues { (_, quote) ->
            quote.copy(isLivePrice = quote.isLivePrice && quote.price > 0)
        }
    }

    suspend fun fetchPerformance(
        symbols: List<String>,
        quotes: Map<String, Quote>,
    ): Map<String, SymbolPerformance> {
        if (symbols.isEmpty()) return emptyMap()
        val distinct = symbols.distinct().map { it.uppercase() }
        return if (usesLiveQuotes) {
            fetchLivePerformance(distinct, quotes)
        } else {
            distinct.associateWith { symbol ->
                PerformanceCalculator.mockPerformance(symbol)
            }
        }
    }

    suspend fun fetchPriceHistory(
        symbols: List<String>,
        days: Int = 30,
    ): Map<String, SymbolPriceHistory> {
        if (symbols.isEmpty()) return emptyMap()
        val distinct = symbols.distinct().map { it.uppercase() }
        return if (usesLiveQuotes) {
            coroutineScope {
                distinct.map { symbol ->
                    async {
                        runCatching { fetchSymbolPriceHistory(symbol, days) }
                            .getOrElse { mockPriceHistory(symbol, days) }
                            .let { symbol to it }
                    }
                }.awaitAll().toMap()
            }
        } else {
            distinct.associateWith { symbol -> mockPriceHistory(symbol, days) }
        }
    }

    private suspend fun fetchLivePerformance(
        symbols: List<String>,
        quotes: Map<String, Quote>,
    ): Map<String, SymbolPerformance> = coroutineScope {
        symbols.map { symbol ->
            async {
                val quote = quotes[symbol]
                val current = quote?.price?.takeIf { it > 0 }
                    ?: buildFallbackQuote(symbol).price
                runCatching { fetchSymbolPerformance(symbol, current) }
                    .getOrElse { PerformanceCalculator.mockPerformance(symbol) }
                    .let { symbol to it }
            }
        }.awaitAll().toMap()
    }

    @OptIn(ExperimentalTime::class)
    private suspend fun fetchSymbolPriceHistory(symbol: String, days: Int): SymbolPriceHistory {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val from = today.minus(DatePeriod(days = days))
        val responseText: String = client.get(FMP_HISTORICAL_URL) {
            parameter("symbol", symbol)
            parameter("from", from.toString())
            parameter("to", today.toString())
            parameter("apikey", apiKey)
        }.bodyAsText()

        if (isFmpErrorResponse(responseText)) return mockPriceHistory(symbol, days)

        val rows = responseJson.decodeFromString<List<FmpHistoricalRow>>(responseText)
            .filter { it.close > 0 }
            .sortedBy { it.date }
            .map { PricePoint(it.date, it.close) }

        if (rows.size < 2) return mockPriceHistory(symbol, days)
        return SymbolPriceHistory(symbol, rows)
    }

    @OptIn(ExperimentalTime::class)
    private fun mockPriceHistory(symbol: String, days: Int): SymbolPriceHistory {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val endPrice = buildFallbackQuote(symbol).price
        val drift = (symbol.hashCode() % 7 - 3) * 0.003
        val points = (days downTo 0).map { offset ->
            val date = today.minus(DatePeriod(days = offset))
            val progress = (days - offset).toDouble() / days.coerceAtLeast(1)
            val noise = kotlin.math.sin(progress * 6.0 + symbol.length) * 0.015
            val price = endPrice * (1.0 - drift * progress + noise)
            PricePoint(date.toString(), kotlin.math.max(price, 0.01))
        }
        return SymbolPriceHistory(symbol, points)
    }

    @OptIn(ExperimentalTime::class)
    private suspend fun fetchSymbolPerformance(symbol: String, currentPrice: Double): SymbolPerformance {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val oneMonthAgo = today.minus(DatePeriod(months = 1))
        val ytdStart = LocalDate(today.year, 1, 1)

        val monthClose = fetchCloseOnOrBefore(symbol, oneMonthAgo)
        val ytdClose = fetchCloseOnOrBefore(symbol, ytdStart)

        return SymbolPerformance(
            return1MonthPercent = monthClose?.let { PerformanceCalculator.returnPercent(currentPrice, it) },
            returnYtdPercent = ytdClose?.let { PerformanceCalculator.returnPercent(currentPrice, it) },
        )
    }

    private suspend fun fetchCloseOnOrBefore(symbol: String, targetDate: LocalDate): Double? {
        val from = targetDate.minus(DatePeriod(days = 7))
        val responseText: String = client.get(FMP_HISTORICAL_URL) {
            parameter("symbol", symbol)
            parameter("from", from.toString())
            parameter("to", targetDate.toString())
            parameter("apikey", apiKey)
        }.bodyAsText()

        if (isFmpErrorResponse(responseText)) return null

        val rows = responseJson.decodeFromString<List<FmpHistoricalRow>>(responseText)
        return rows
            .filter { it.close > 0 }
            .maxByOrNull { it.date }
            ?.close
    }

    private suspend fun fetchLiveQuotes(
        symbols: List<String>,
        allowEstimatedFallback: Boolean,
    ): Map<String, Quote> {
        val liveQuotes = mutableMapOf<String, Quote>()

        runCatching { fetchBatchQuotesStable(symbols) }
            .getOrDefault(emptyMap())
            .forEach { (symbol, quote) -> liveQuotes[symbol] = quote }

        val stillMissing = symbols.filter { liveQuotes[it]?.price?.let { price -> price > 0 } != true }
        if (stillMissing.isNotEmpty()) {
            coroutineScope {
                stillMissing.map { symbol ->
                    async {
                        runCatching { fetchSingleQuoteStable(symbol, fetchDividend = false) }
                            .getOrNull()
                            ?.takeIf { it.price > 0 }
                    }
                }.awaitAll().filterNotNull().forEach { quote ->
                    liveQuotes[quote.symbol] = quote
                }
            }
        }

        val missingAfterQuotes = symbols.filter { liveQuotes[it]?.price?.let { price -> price > 0 } != true }
        if (missingAfterQuotes.isNotEmpty()) {
            coroutineScope {
                missingAfterQuotes.map { symbol ->
                    async {
                        runCatching { fetchLatestHistoricalClose(symbol) }
                            .getOrNull()
                            ?.takeIf { it > 0 }
                            ?.let { close ->
                                Quote(
                                    symbol = symbol.uppercase(),
                                    price = close,
                                    name = symbol.uppercase(),
                                    isLivePrice = true,
                                )
                            }
                    }
                }.awaitAll().filterNotNull().forEach { quote ->
                    liveQuotes[quote.symbol] = quote
                }
            }
        }

        if (!allowEstimatedFallback) return liveQuotes

        val missingAfterLive = symbols.filter { liveQuotes[it]?.price?.let { price -> price > 0 } != true }
        val fallback = if (missingAfterLive.isEmpty()) {
            emptyMap()
        } else {
            fetchMockQuotes(missingAfterLive, pricedForDisplay = false)
        }
        return liveQuotes + fallback
    }

    @OptIn(ExperimentalTime::class)
    private suspend fun fetchLatestHistoricalClose(symbol: String): Double? {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val from = today.minus(DatePeriod(days = 10))
        val responseText: String = client.get(FMP_HISTORICAL_URL) {
            parameter("symbol", symbol)
            parameter("from", from.toString())
            parameter("to", today.toString())
            parameter("apikey", apiKey)
        }.bodyAsText()

        if (isFmpErrorResponse(responseText)) return null

        return responseJson.decodeFromString<List<FmpHistoricalRow>>(responseText)
            .filter { it.close > 0 }
            .maxByOrNull { it.date }
            ?.close
    }

    private suspend fun fetchBatchQuotesStable(symbols: List<String>): Map<String, Quote> {
        if (symbols.isEmpty()) return emptyMap()

        return symbols
            .distinct()
            .map { it.uppercase() }
            .chunked(BATCH_QUOTE_SIZE)
            .flatMap { chunk -> fetchBatchQuotesStableChunk(chunk).entries }
            .associate { it.key to it.value }
    }

    private suspend fun fetchBatchQuotesStableChunk(symbols: List<String>): Map<String, Quote> {
        if (symbols.isEmpty()) return emptyMap()

        val responseText: String = client.get(FMP_BATCH_QUOTE_URL) {
            parameter("symbols", symbols.joinToString(","))
            parameter("apikey", apiKey)
        }.bodyAsText()

        if (isFmpErrorResponse(responseText)) return emptyMap()

        return decodeQuoteRows(responseText)
            .mapNotNull(::fmpQuoteToLiveQuote)
            .associateBy { it.symbol }
    }

    private fun decodeQuoteRows(responseText: String): List<FmpQuoteResponse> =
        runCatching { responseJson.decodeFromString<List<FmpQuoteResponse>>(responseText) }
            .getOrElse {
                runCatching { responseJson.decodeFromString<FmpQuoteResponse>(responseText) }
                    .map { listOf(it) }
                    .getOrDefault(emptyList())
            }

    private fun fmpQuoteToLiveQuote(row: FmpQuoteResponse): Quote? {
        val price = resolveLiveQuotePrice(row.price, row.previousClose)
        if (price <= 0) return null
        val symbol = row.symbol.uppercase()
        return Quote(
            symbol = symbol,
            price = price,
            changePercent = row.changePercentage ?: row.changesPercentage ?: 0.0,
            dividendYield = 0.0,
            name = row.name.orEmpty(),
            yearHigh = row.yearHigh,
            yearLow = row.yearLow,
            peRatio = row.pe,
            isLivePrice = true,
        )
    }

    private suspend fun fetchSingleQuoteStable(symbol: String, fetchDividend: Boolean = true): Quote {
        val responseText: String = client.get(FMP_QUOTE_URL) {
            parameter("symbol", symbol)
            parameter("apikey", apiKey)
        }.bodyAsText()

        if (isFmpErrorResponse(responseText)) {
            throw IllegalStateException("FMP API error for $symbol")
        }

        val quote = decodeQuoteRows(responseText).firstOrNull()
            ?: throw IllegalStateException("No quote returned for $symbol")

        val price = resolveLiveQuotePrice(quote.price, quote.previousClose)
        if (price <= 0) throw IllegalStateException("No usable price returned for $symbol")

        val dividendYield = if (fetchDividend) {
            runCatching { fetchDividendYield(symbol, price) }.getOrDefault(0.0)
        } else {
            0.0
        }

        return Quote(
            symbol = symbol.uppercase(),
            price = price,
            changePercent = quote.changePercentage ?: quote.changesPercentage ?: 0.0,
            dividendYield = dividendYield,
            name = quote.name.orEmpty(),
            yearHigh = quote.yearHigh,
            yearLow = quote.yearLow,
            peRatio = quote.pe,
            isLivePrice = true,
        )
    }

    private fun isFmpErrorResponse(responseText: String): Boolean =
        responseText.contains("Error Message", ignoreCase = true) ||
            responseText.contains("Legacy Endpoint", ignoreCase = true)

    private suspend fun fetchDividendYield(symbol: String, price: Double): Double {
        if (price <= 0) return 0.0

        val ratiosText: String = client.get(FMP_RATIOS_URL) {
            parameter("symbol", symbol)
            parameter("apikey", apiKey)
        }.bodyAsText()

        if (!isFmpErrorResponse(ratiosText)) {
            val ratios = responseJson.decodeFromString<List<FmpRatioResponse>>(ratiosText)
            ratios.firstOrNull()?.dividendYield?.let { yield ->
                return if (yield <= 1.0) yield * 100 else yield
            }
        }

        val profilesText: String = client.get(FMP_PROFILE_URL) {
            parameter("symbol", symbol)
            parameter("apikey", apiKey)
        }.bodyAsText()

        if (isFmpErrorResponse(profilesText)) return 0.0

        val profiles = responseJson.decodeFromString<List<FmpProfileResponse>>(profilesText)
        val lastDividend = profiles.firstOrNull()?.lastDividend ?: return 0.0
        return (lastDividend / price) * 100
    }

    private fun fetchMockQuotes(symbols: List<String>, pricedForDisplay: Boolean = false): Map<String, Quote> =
        symbols.distinct().associate { symbol ->
            val upper = symbol.uppercase()
            upper to buildFallbackQuote(upper, pricedForDisplay = pricedForDisplay)
        }

    private fun buildFallbackQuote(symbol: String, pricedForDisplay: Boolean = false): Quote {
        val mock = MOCK_QUOTES[symbol] ?: MockQuote(
            price = 100.0 + (symbol.hashCode() % 25),
            changePercent = 0.0,
            dividendYield = 1.5,
            name = symbol,
        )
        return Quote(
            symbol = symbol,
            price = mock.price,
            changePercent = mock.changePercent,
            dividendYield = mock.dividendYield,
            name = mock.name,
            yearHigh = mock.price * 1.08,
            yearLow = mock.price * 0.92,
            peRatio = null,
            isLivePrice = pricedForDisplay,
        )
    }

    companion object {
        private const val KEY_API = "fmp_api_key"
        private const val LEGACY_KEY_API = "finnhub_api_key"
        private val FMP_QUOTE_URL get() = fmpStableUrl("quote")
        private val FMP_BATCH_QUOTE_URL get() = fmpStableUrl("batch-quote")
        private const val BATCH_QUOTE_SIZE = 25
        private val FMP_PROFILE_URL get() = fmpStableUrl("profile")
        private val FMP_RATIOS_URL get() = fmpStableUrl("ratios")
        private val FMP_HISTORICAL_URL get() = fmpStableUrl("historical-price-eod/full")

        private val responseJson = Json {
            ignoreUnknownKeys = true
            isLenient = true
        }

        private data class MockQuote(
            val price: Double,
            val changePercent: Double,
            val dividendYield: Double,
            val name: String,
        )

        // Demo prices are current-market estimates, intentionally different from typical CSV purchase prices.
        private val MOCK_QUOTES = mapOf(
            "VTI" to MockQuote(392.15, 0.45, 1.28, "Vanguard Total Stock Market ETF"),
            "SCHD" to MockQuote(35.42, 0.25, 3.45, "Schwab US Dividend Equity ETF"),
            "VXUS" to MockQuote(87.90, 0.18, 2.95, "Vanguard Total International Stock ETF"),
            "SCHG" to MockQuote(35.88, 0.62, 0.45, "Schwab US Large-Cap Growth ETF"),
            "QQQM" to MockQuote(298.50, 0.88, 0.55, "Invesco NASDAQ 100 ETF"),
            "JEPI" to MockQuote(58.75, 0.12, 7.85, "JPMorgan Equity Premium Income ETF"),
            "JEPQ" to MockQuote(59.20, 0.15, 9.25, "JPMorgan NASDAQ Equity Premium Income ETF"),
            "AMDY" to MockQuote(43.50, -0.35, 12.5, "YieldMax AMD Option Income Strategy ETF"),
            "SMCY" to MockQuote(4.72, 1.2, 35.0, "YieldMax SMCI Option Income Strategy ETF"),
            "AAPL" to MockQuote(227.5, 0.82, 0.44, "Apple Inc."),
            "MSFT" to MockQuote(425.0, 1.12, 0.67, "Microsoft Corp."),
            "AVGO" to MockQuote(368.56, 0.55, 0.72, "Broadcom Inc."),
            "NVDA" to MockQuote(120.0, 1.25, 0.02, "NVIDIA Corp."),
            "GOOGL" to MockQuote(175.0, 0.65, 0.42, "Alphabet Inc."),
            "AMZN" to MockQuote(195.0, 0.48, 0.0, "Amazon.com Inc."),
            "JPM" to MockQuote(245.0, 0.35, 2.1, "JPMorgan Chase"),
            "V" to MockQuote(280.0, 0.22, 0.75, "Visa Inc."),
            "KO" to MockQuote(65.0, 0.15, 2.9, "Coca-Cola Co."),
            "PG" to MockQuote(165.0, 0.18, 2.4, "Procter & Gamble"),
            "VOO" to MockQuote(520.0, 0.42, 1.25, "Vanguard S&P 500 ETF"),
            "AGG" to MockQuote(98.0, 0.05, 3.2, "iShares Core US Aggregate Bond ETF"),
            "BND" to MockQuote(72.5, 0.08, 3.85, "Vanguard Total Bond Market ETF"),
        )
    }
}

@Serializable
private data class FmpHistoricalRow(
    val date: String = "",
    val close: Double = 0.0,
)

@Serializable
private data class FmpQuoteResponse(
    val symbol: String = "",
    val name: String? = null,
    val price: Double = 0.0,
    val previousClose: Double? = null,
    val changePercentage: Double? = null,
    val changesPercentage: Double? = null,
    val yearHigh: Double? = null,
    val yearLow: Double? = null,
    val pe: Double? = null,
)

@Serializable
private data class FmpProfileResponse(
    val lastDividend: Double? = null,
)

@Serializable
private data class FmpRatioResponse(
    val dividendYield: Double? = null,
)
